import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, Circle, Lock, Menu, Smartphone, icons } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Logo, ThemeToggle } from "@/components/common";
import { useConfig } from "@/context/ConfigContext";
import { useAuth, homeFor } from "@/context/AuthContext";
import { appHref, consoleHref, isExternal } from "@/lib/hosts";

const SmartLink = ({ to, children, ...p }) => (isExternal(to) ? <a href={to} {...p}>{children}</a> : <Link to={to} {...p}>{children}</Link>);
import { SECTIONS, menuPages, pathForSlug } from "@/lib/site";
import { SiteSearch } from "@/components/public/SiteSearch";

const Icon = ({ name, size = 18 }) => {
  const I = icons[name] || Circle;
  return <I size={size} strokeWidth={1.7} />;
};

function Dropdown({ section, pages }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const ref = useRef(null);
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  const mega = section.id === "plateforme";
  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button data-testid={`nav-${section.id}`} onClick={() => setOpen(!open)} aria-expanded={open} className={`mr-link inline-flex items-center gap-1 text-sm font-medium h-16 ${loc.pathname.startsWith(section.to.split("/").slice(0, 2).join("/")) ? "active" : ""}`}>
        {section.label} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-1 z-50 ${mega ? "w-[760px]" : "w-80"}`} data-testid={`nav-${section.id}-menu`}>
          <div className={`border border-mr-border bg-mr-bg p-3 shadow-2xl ${mega ? "grid grid-cols-3 gap-1" : "space-y-1"}`}>
            {pages.map((p) => (
              <Link key={p.slug} to={pathForSlug(p.slug)} data-testid={`nav-item-${p.slug.replace(/\//g, "-")}`} className="flex gap-3 p-3 rounded-[3px] hover:bg-mr-surface transition-colors">
                <span className="text-mr-accent mt-0.5"><Icon name={p.icon} /></span>
                <span><span className="block text-sm font-medium">{p.nav_label}</span>{p.tagline && <span className="block text-xs text-mr-muted mt-0.5">{p.tagline}</span>}</span>
              </Link>
            ))}
            {mega && <Link to="/plateforme" className="col-span-3 mt-1 p-3 border-t border-mr-border text-sm text-mr-accent inline-flex items-center gap-1">Découvrir toute la plateforme <ArrowRight size={14} /></Link>}
          </div>
        </div>
      )}
    </div>
  );
}

function Header() {
  const { me } = useAuth();
  const { pages } = useConfig();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-mr-border bg-[color-mix(in_srgb,var(--mr-bg)_88%,transparent)] backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7">{SECTIONS.map((s) => <Dropdown key={s.id} section={s} pages={menuPages(pages, s.id)} />)}</nav>
        <div className="flex items-center gap-2">
          <SiteSearch />
          <ThemeToggle />
          <SmartLink to={me ? homeFor(me) : appHref("/app")} data-testid="header-login-button" className="mr-btn-ghost hidden sm:inline-flex">{me ? "Mon espace" : "Connexion"}</SmartLink>
          <Link to="/demande-invitation" data-testid="header-invitation-button" className="mr-btn-primary hidden md:inline-flex">Demander une invitation <ArrowRight size={16} /></Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><button data-testid="public-mobile-menu-button" aria-label="Menu" className="lg:hidden grid place-items-center w-11 h-11 border border-mr-border rounded-[3px]"><Menu size={18} /></button></SheetTrigger>
            <SheetContent side="right" className="bg-mr-bg border-mr-border overflow-y-auto">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <Accordion type="single" collapsible className="mt-8">
                {SECTIONS.map((s) => (
                  <AccordionItem key={s.id} value={s.id} className="border-mr-border">
                    <AccordionTrigger data-testid={`mobile-nav-${s.id}`} className="text-base hover:no-underline">{s.label}</AccordionTrigger>
                    <AccordionContent><div className="flex flex-col">{menuPages(pages, s.id).map((p) => (
                      <Link key={p.slug} to={pathForSlug(p.slug)} onClick={() => setOpen(false)} className="flex items-center gap-3 py-2.5 text-mr-text2"><span className="text-mr-accent"><Icon name={p.icon} size={16} /></span>{p.nav_label}</Link>))}</div></AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              <div className="flex flex-col gap-2 mt-6">
                <SmartLink to={me ? homeFor(me) : appHref("/app")} onClick={() => setOpen(false)} data-testid="mobile-login-link" className="mr-btn-secondary">{me ? "Mon espace" : "Connexion"}</SmartLink>
                <Link to="/demande-invitation" onClick={() => setOpen(false)} data-testid="mobile-invitation-link" className="mr-btn-primary">Demander une invitation <ArrowRight size={16} /></Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const { settings, pages } = useConfig();
  const cols = [...SECTIONS, { id: "legal", label: "Légal" }];
  const social = (settings["site.social_links"] || []).map((l) => l.split("|").map((x) => x.trim())).filter((x) => x[1]);
  return (
    <footer className="border-t border-mr-border mt-16 bg-mr-surface" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid gap-10 lg:grid-cols-[1.2fr_3fr]">
        <div>
          <Logo testId="footer-logo-link" />
          <p className="text-sm text-mr-muted mt-3 max-w-xs">{settings["texts.tagline"]}</p>
          <div className="flex flex-wrap gap-2 mt-5">{social.map(([n, u]) => <a key={n} href={u} target="_blank" rel="noreferrer" data-testid={`footer-social-${n}`} className="text-xs px-3 py-1.5 border border-mr-border rounded-full text-mr-text2 hover:text-mr-text">{n}</a>)}</div>
          <Link to="/ressources/centre-aide" data-testid="footer-install-app" className="mr-btn-secondary mt-5"><Smartphone size={16} /> Installer l'application</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8">
          {cols.map((c) => (
            <div key={c.id}><p className="text-xs uppercase tracking-wider text-mr-muted mb-4">{c.label}</p>
              <ul className="space-y-2.5">{menuPages(pages, c.id).map((p) => <li key={p.slug}><NavLink to={pathForSlug(p.slug)} className="mr-link text-sm">{p.nav_label}</NavLink></li>)}</ul></div>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 border-t border-mr-border flex flex-wrap items-center justify-between gap-4 text-xs text-mr-muted">
        <p data-testid="footer-notice" className="max-w-3xl">{settings["texts.footer_notice"]}</p>
        <div className="flex items-center gap-4"><span>© {new Date().getFullYear()} Monrezo · Abidjan</span>
          <SmartLink to={consoleHref("/console")} aria-label="Administration" data-testid="footer-console-link" className="opacity-30 hover:opacity-70 transition-opacity"><Lock size={12} /></SmartLink></div>
      </div>
    </footer>
  );
}

export default function SiteLayout() {
  return (
    <div className="min-h-screen mr-grid-bg flex flex-col">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
